/* Attached photos, or a video, on a card.
 *
 * The layout follows the count, the way every social product does it: one fills
 * the width, two split it, three put one large beside two stacked, four make a
 * square. Anything else and a single portrait photo dominates the feed while
 * four look like a contact sheet.
 *
 * Every tile reserves its space with an aspect ratio, so the card does not jump
 * when the images arrive — that is what the width and height on each media row
 * are for.
 *
 * A VIDEO IS SHOWN ON ITS OWN. A post carries photos or one video, and a video
 * squeezed into a quarter of a grid is one nobody can watch.
 */
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Media } from '@/types/api'

export function MediaGrid({ media }: { media: Media[] }) {
  if (!media.length) return null

  const video = media.find((m) => m.kind === 'video')
  if (video) return <VideoTile video={video} />

  if (media.length === 1) {
    const only = media[0]
    // The ratio reserves the space so the card does not jump when the image
    // arrives. It is the photo's TRUE shape — .cn-photo letterboxes rather
    // than crops, so there is nothing to gain from lying about it, and the
    // old 0.75 floor squashed tall images into a box they did not fit.
    // The height cap lives in .cn-photo, against the viewport.
    const ratio =
      only.width && only.height ? only.width / only.height : 4 / 3
    return (
      <img
        src={only.url ?? undefined}
        alt=""
        loading="lazy"
        style={{ aspectRatio: ratio }}
        className="cn-photo"
      />
    )
  }

  return (
    <div
      className={cn(
        'grid gap-0.5 bg-surface-2',
        media.length === 2 && 'grid-cols-2',
        media.length === 3 && 'grid-cols-2',
        media.length >= 4 && 'grid-cols-2',
      )}
    >
      {media.slice(0, 4).map((m, i) => (
        <img
          key={m.id}
          src={m.url ?? undefined}
          alt=""
          loading="lazy"
          className={cn(
            'w-full object-cover',
            // Three photos: the first spans both rows on the left.
            media.length === 3 && i === 0 ? 'row-span-2 h-full' : 'h-[190px]',
          )}
        />
      ))}
    </div>
  )
}

/** One video. Nothing plays until it is tapped, and nothing ever plays with
 *  sound on its own — a feed that starts talking is a feed people mute. */
function VideoTile({ video }: { video: Media }) {
  const ratio =
    video.width && video.height ? video.width / video.height : 16 / 9

  /* Its uploader's own view while it is being prepared. Everybody else is
     not sent it until it is ready, so this is never a stranger's spinner. */
  if (video.status !== 'ready' || !video.url) {
    return (
      <div
        style={{ aspectRatio: 16 / 9 }}
        className="grid w-full place-items-center bg-surface-2 text-center"
      >
        <div className="px-s4">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-ink-3" aria-hidden />
          <p className="mt-s2 text-base font-semibold text-ink-2">
            Preparing your video…
          </p>
          <p className="mt-0.5 text-sm text-ink-3">
            Only you can see this until it is ready. It usually takes under a
            minute.
          </p>
        </div>
      </div>
    )
  }

  return (
    <video
      src={video.url}
      poster={video.poster_url ?? undefined}
      controls
      playsInline
      // Only the index is fetched until somebody presses play, so a feed of
      // videos costs a feed of posters rather than a feed of downloads.
      preload="metadata"
      style={{ aspectRatio: ratio }}
      className="cn-photo bg-black"
    />
  )
}
